import React, {useEffect, useMemo, useRef} from 'react';
import echarts from '@/utils/echartsCore';
import {AggregationType, BarOrientation, ChartFieldToQueryOption, ChartOutput, DataLabelPosition, PartialChartQuery} from '@/entities/chart';
import {chartMetricTimelineTypes} from '@/views/storyboard/utils/chartConstants';
import {isTimelineChartOutput} from '@/views/storyboard/utils/chartSettingsFieldVisibility';
import {serialize} from '@/views/storyboard/utils/helpers';
import {buildChartImageDownloadToolboxFeatures} from '@/views/storyboard/utils/chartImageDownload';
import {
    buildBarDataLabelConfig,
    buildLineDataLabelConfig,
    buildStackedLabelContext,
    getLinePointSymbolConfig,
} from '@/views/storyboard/utils/chartDataLabels';
import i18n from 'i18n';

const ESTIMATED_CHAR_WIDTH_PX = 7;
const VERTICAL_LABEL_ROTATION_DEGREES = 45;

function getCategoryLabelStrings(data: unknown[], xAxisName: string | number): string[] {
    if (!Array.isArray(data)) {
        return [];
    }

    return data.map((item) => String((item as Record<string, unknown>)[xAxisName] ?? ''));
}

function estimateVerticalChartWidthPx(width: number, chartSettingsView: boolean): number {
    if (chartSettingsView) {
        return 360;
    }
    if (width <= 1) {
        return 280;
    }
    if (width === 2) {
        return 560;
    }
    if (width === 3) {
        return 840;
    }
    if (width === 4) {
        return 1120;
    }

    return 1400;
}

function shouldRotateVerticalCategoryLabels(
    labels: string[],
    width: number,
    chartSettingsView: boolean,
): boolean {
    if (labels.length <= 1) {
        return false;
    }

    const slotWidthPx = estimateVerticalChartWidthPx(width, chartSettingsView) / labels.length;
    const longestLabelPx = Math.max(...labels.map((label) => label.length * ESTIMATED_CHAR_WIDTH_PX));

    return longestLabelPx > slotWidthPx * 0.9;
}

function getRotatedVerticalGridBottom(maxLabelLength: number): string {
    if (maxLabelLength > 18) {
        return '28%';
    }
    if (maxLabelLength > 12) {
        return '24%';
    }

    return '20%';
}

function parseGridBottomPercent(value: string): number {
    return Number.parseFloat(value.replace('%', '')) || 0;
}

export interface BarChartProps {
    data: any;
    distinctKeysArray: string[];
    chartQuery: PartialChartQuery;
    finalChartQuery: any;
    fieldToQuery?: ChartFieldToQueryOption;
    comparablePeriod?: boolean;
    movingAverage: number | null | undefined;
    linearRegression?: boolean;
    xAxisName: string | number;
    barOrientation?: BarOrientation;
    dataLabelPosition?: DataLabelPosition;
    height?: number;
    width?: number;
    onLegendToggle?: (hiddenKeys: string[]) => void;
    chartSettingsView?: boolean;
    title?: string;
    description?: string;
    datePeriod?: string;
}

const BarCharts: React.FC<BarChartProps> = (
    {
        data,
        distinctKeysArray,
        chartQuery,
        fieldToQuery,
        finalChartQuery,
        comparablePeriod,
        movingAverage,
        linearRegression,
        xAxisName,
        barOrientation = BarOrientation.Vertical,
        dataLabelPosition = DataLabelPosition.Off,
        height = 1,
        width = 1,
        onLegendToggle,
        chartSettingsView,
        title = '',
        description = '',
        datePeriod = '',
    }
) => {

    const chartRef = useRef(null);

    const showToolbox = !chartSettingsView && height !== 1;

    const {chartHeight, gridBottom} = useMemo(() => {
        const chartHeight = '100%';
        let gridBottom = '30%';

        if (height === 2 || height === 3) {
            gridBottom = '20%';
        } else if (height === 4 || height === 5) {
            gridBottom = chartSettingsView ? '20%' : '15%';
        }

        return {chartHeight, gridBottom};
    }, [height, chartSettingsView]);

    const comparableCountData = useMemo(() => data.map((item: any) => item.comparableDocCount), [data]);
    const movingAverageData = useMemo(() => data.map((item: any) => item.movingAvg), [data]);
    const linearRegressionData = useMemo(() => data.map((item: any) => item.linearReg), [data]);

    const isHorizontal = barOrientation === BarOrientation.Horizontal;
    const isTermsOutput = chartQuery.output === ChartOutput.Terms;
    const isTimelineOutput = isTimelineChartOutput(chartQuery.output);
    const showTimelineOverlays = !isTermsOutput && chartQuery.output !== ChartOutput.TermsMetric;
    const horizontalCategoryLabelWidth = useMemo(() => {
        if (!isHorizontal) {
            return undefined;
        }

        if (chartSettingsView) {
            return 150;
        }

        if (width <= 1) {
            return 90;
        }
        if (width === 2) {
            return 180;
        }
        if (width === 3) {
            return 240;
        }

        return 300;
    }, [chartSettingsView, isHorizontal, width]);

    const verticalCategoryLabels = useMemo(
        () => getCategoryLabelStrings(data, xAxisName),
        [data, xAxisName],
    );

    const rotateVerticalCategoryLabels = useMemo(() => {
        if (isHorizontal || isTimelineOutput) {
            return false;
        }

        return shouldRotateVerticalCategoryLabels(verticalCategoryLabels, width, chartSettingsView ?? false);
    }, [isHorizontal, isTimelineOutput, verticalCategoryLabels, width, chartSettingsView]);

    const categoryAxisLabelConfig = useMemo(() => {
        const categoryCount = Array.isArray(data) ? data.length : 0;

        if (isTimelineOutput) {
            return {
                fontFamily: '"Rubik", sans-serif',
                fontSize: 13,
            };
        }

        if (!isHorizontal) {
            return {
                fontFamily: '"Rubik", sans-serif',
                interval: 0,
                hideOverlap: false,
                fontSize: categoryCount > 20 ? 10 : categoryCount > 12 ? 11 : 13,
                ...(rotateVerticalCategoryLabels ? {
                    rotate: VERTICAL_LABEL_ROTATION_DEGREES,
                    margin: 12,
                } : {}),
            };
        }

        return {
            fontFamily: '"Rubik", sans-serif',
            interval: 0,
            hideOverlap: false,
            fontSize: categoryCount > 18 ? 9 : categoryCount > 12 ? 10 : categoryCount > 8 ? 11 : 13,
            width: horizontalCategoryLabelWidth,
            overflow: 'truncate' as const,
            ellipsis: '...',
        };
    }, [data, horizontalCategoryLabelWidth, isHorizontal, isTimelineOutput, rotateVerticalCategoryLabels]);

    const chartGrid = useMemo(() => {
        if (!isHorizontal) {
            let bottom = gridBottom;

            if (rotateVerticalCategoryLabels) {
                const maxLabelLength = Math.max(...verticalCategoryLabels.map((label) => label.length), 0);
                const rotatedBottom = getRotatedVerticalGridBottom(maxLabelLength);
                bottom = parseGridBottomPercent(rotatedBottom) > parseGridBottomPercent(gridBottom)
                    ? rotatedBottom
                    : gridBottom;
            }

            return {
                top: '6%',
                left: '4%',
                right: '6%',
                bottom,
                containLabel: true,
            };
        }

        const labelLaneWidth = horizontalCategoryLabelWidth ?? 180;

        return {
            top: '10%',
            left: labelLaneWidth + 16,
            right: showToolbox ? 52 : 24,
            bottom: isTermsOutput ? '14%' : gridBottom,
            containLabel: false,
        };
    }, [horizontalCategoryLabelWidth, isHorizontal, showToolbox, isTermsOutput, gridBottom, rotateVerticalCategoryLabels, verticalCategoryLabels]);

    const showLegend = !(isHorizontal && isTermsOutput && distinctKeysArray.length <= 1);

    const getSeriesLabel = (dataKey: string): string => {
        if (dataKey !== 'docCount') {
            return dataKey;
        }

        if (isTermsOutput
            && chartQuery.metric_aggregations_aggs_item
            && chartQuery.metric_aggregations_type
            && chartQuery.metric_aggregations_type !== AggregationType.Count) {
            return chartMetricTimelineTypes.find((entry) => entry[0] === chartQuery.metric_aggregations_type)?.[1]
                ?? dataKey;
        }

        return i18n.t('ch_count.text');
    };

    const baseLegend = useMemo(() =>
        distinctKeysArray.map((key) => getSeriesLabel(key)), [distinctKeysArray, chartQuery, isTermsOutput]);

    const extraLegendKeys = useMemo(() => {
        const extras = [];
        if (chartQuery.output === 'timeline_by_date' && comparablePeriod) {
            extras.push(i18n.t('ch_comparable_count.text'));
        }
        if (typeof movingAverage === 'number') {
            extras.push(i18n.t('ch_moving_average_label.text', {movingAverageDays: movingAverage}));
        }
        if (linearRegression) {
            extras.push(i18n.t('ch_trendline.text'));
        }
        return extras;
    }, [chartQuery.output, comparablePeriod, movingAverage, linearRegression]);

    const legendData = useMemo(() => [...baseLegend, ...extraLegendKeys], [baseLegend, extraLegendKeys]);

    const stackedLabelContext = useMemo(() => {
        if (dataLabelPosition === DataLabelPosition.Off || isTermsOutput || !Array.isArray(data) || !data.length) {
            return null;
        }

        return buildStackedLabelContext(
            distinctKeysArray,
            data as Record<string, unknown>[],
            height,
            width,
        );
    }, [data, distinctKeysArray, dataLabelPosition, isTermsOutput, height, width]);

    const overlayLineLabelConfig = useMemo(
        () => buildLineDataLabelConfig(dataLabelPosition),
        [dataLabelPosition],
    );

    const linePointSymbol = useMemo(
        () => getLinePointSymbolConfig(dataLabelPosition),
        [dataLabelPosition],
    );

    useEffect(() => {
        if (!chartRef.current) {return;}

        const barchart = echarts.init(chartRef.current);
        const handleResize = () => {
            if (barchart && !barchart.isDisposed()) {
                barchart.resize();
            }
        };

        const handleChatSidebarChange = () => {
            // Add a small delay to allow CSS transitions to complete
            setTimeout(handleResize, 350);
        };

        // Observe container size changes
        let resizeObserver: any = null;
        if (typeof window !== 'undefined' && 'ResizeObserver' in window && chartRef.current) {
            resizeObserver = new (window as any).ResizeObserver(() => {
                handleResize();
            });
            resizeObserver.observe(chartRef.current);
        }

        window.addEventListener('resize', handleResize);
        window.addEventListener('chatSidebarOpened', handleChatSidebarChange);
        window.addEventListener('chatSidebarClosed', handleChatSidebarChange);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('chatSidebarOpened', handleChatSidebarChange);
            window.removeEventListener('chatSidebarClosed', handleChatSidebarChange);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (barchart && !barchart.isDisposed()) {
                barchart.dispose();
            }
        }
    }, []);

    // Ensure the chart is resized when the container height changes
    useEffect(() => {
        if (!chartRef.current) {return;}

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {return;}

        chartInstance.resize();
    }, [height]);

    useEffect(() => {
        if (!chartRef.current) {return;}

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {return;}

        const categoryLabels = data.map((item: any) => item[xAxisName]);

        const categoryAxis = {
            type: 'category',
            data: categoryLabels,
            axisLabel: categoryAxisLabelConfig,
            ...(isHorizontal ? {inverse: true} : {}),
        };

        const valueAxis = {
            type: 'value',
            min: 0,
            axisTick: {
                alignWithLabel: true,
            },
            axisLabel: {
                fontFamily: '"Rubik", sans-serif',
                fontSize: 13,
            },
        };

        const option = {
            textStyle: {
                fontFamily: '"Rubik", sans-serif',
                fontSize: 13,
                fontWeight: 100,
            },
            tooltip: {
                trigger: 'axis',
                confine: true,
                axisPointer: {
                    type: 'shadow',
                    label: {
                        backgroundColor: '#6a7985'
                    }
                },
                textStyle: {
                    fontWeight: 400,
                },
                formatter: function (params: any) {
                    const axisLabel = params[0]?.axisValue ?? params[0]?.axisValueLabel ?? '';
                    let result = axisLabel + '<br/><table>';

                    (params as Array<any>).forEach((item) => {
                        if (item.data != null && item.data !== '' && !isNaN(item.data)) {
                            result += `
                                <tr>
                                    <td style="text-align: left;">${item.marker}${item.seriesName}</td>
                                    <td style="text-align: right; padding-left: 30px;">${item.data}</td>
                                </tr>
                            `;
                        }
                    });

                    result += '</table>';
                    return result;
                },
            },
            toolbox: {
                show: showToolbox,
                orient: 'vertical',
                itemSize: 12,
                itemGap: 8,
                feature: {
                    dataZoom: {
                        [isHorizontal ? 'xAxisIndex' : 'yAxisIndex']: 'none',
                    },
                    dataView: {
                        optionToContent: function (opt: any) {
                            const categoryAxis = isHorizontal ? opt.yAxis[0] : opt.xAxis[0];
                            const axisData = categoryAxis.data;
                            const series = opt.series;
                            const headerColumns = opt.series.map((x: {
                                name: {toString: () => string;};
                            }) => '<td>' + x.name.toString() + '</td>').join('');
                            let table =
                                '<table class="table table-hover g-table w-100" style="font-size:0.8em">' +
                                '<thead><tr>' +
                                '<td>' + i18n.t('ch_datapoint.text') + '</td>' +
                                headerColumns +
                                '</tr><thead><tbody>';
                            for (let i = 0, l = axisData.length - 1; i < l; i++) {
                                table += '<tr><td>' + axisData[i] + '</td>'
                                for (let j = 0; j < series.length; j++) {
                                    if (series[j].data[i]) {
                                        table += '<td>' + series[j].data[i] + '</td>'
                                    } else {
                                        table += '<td></td>'
                                    }
                                }
                                table += '</tr>';
                            }
                            table += '</tbody></table>';
                            return table;
                        },
                        readOnly: true,
                    },
                    ...buildChartImageDownloadToolboxFeatures({
                        title,
                        description,
                        datePeriod,
                        getChartInstance: () => (
                            chartRef.current ? echarts.getInstanceByDom(chartRef.current) : null
                        ),
                    }, showToolbox),
                },
            },
            legend: showLegend ? {
                type: 'scroll',
                orient: 'horizontal',
                data: legendData,
                selectedMode: 'multiple',
                bottom: '8%',
            } : {show: false},
            grid: chartGrid,
            xAxis: isHorizontal ? valueAxis : categoryAxis,
            yAxis: isHorizontal ? categoryAxis : valueAxis,
            series: [
                ...(Array.isArray(distinctKeysArray) ? distinctKeysArray.map((dataKey: any, seriesIndex: number) => {
                    const label = buildBarDataLabelConfig(dataLabelPosition, isHorizontal, {
                        stackedContext: stackedLabelContext,
                        seriesIndex,
                    });

                    return {
                        type: 'bar',
                        stack: isTermsOutput ? undefined : 'barchart',
                        name: getSeriesLabel(dataKey),
                        data: Array.isArray(data) ? data.map((item: any) => item[dataKey]) : [],
                        barWidth: isHorizontal ? undefined : '60%',
                        ...(label ? {label} : {}),
                    };
                }) : []),

                showTimelineOverlays && chartQuery.output === 'timeline_by_date' && comparablePeriod ? {
                    type: 'line',
                    encode: {x: 'labelShort', y: 'comparableDocCount'},
                    name: i18n.t('ch_comparable_count.text'),
                    data: comparableCountData,
                    lineStyle: {
                        type: 'dashed',
                    },
                    ...linePointSymbol,
                    connectNulls: true,
                    ...(overlayLineLabelConfig ? {label: overlayLineLabelConfig} : {}),
                } : null,

                showTimelineOverlays && movingAverage != null ? {
                    type: 'line',
                    encode: {x: 'labelShort', y: 'movingAvg'},
                    name: i18n.t('ch_moving_average_label.text', {movingAverageDays: movingAverage}),
                    data: movingAverageData,
                    ...linePointSymbol,
                    ...(overlayLineLabelConfig ? {label: overlayLineLabelConfig} : {}),
                } : null,

                showTimelineOverlays && linearRegression ? {
                    type: 'line',
                    encode: {x: 'labelShort', y: 'linearReg'},
                    name: i18n.t('ch_trendline.text'),
                    data: linearRegressionData,
                    ...linePointSymbol,
                    connectNulls: true,
                    ...(overlayLineLabelConfig ? {label: overlayLineLabelConfig} : {}),
                } : null,
            ].filter(Boolean)
        };

        chartInstance.setOption(option, {replaceMerge: ['series']});

        chartInstance.on('legendselectchanged', function (params: any) {
            const hiddenSeries = [];

            for (let [seriesName, isSelected] of Object.entries(params.selected)) {

                if (seriesName === i18n.t('ch_count.text')) {
                    seriesName = 'docCount';
                }

                if (!isSelected) {
                    hiddenSeries.push(seriesName);
                }
            }

            onLegendToggle?.(hiddenSeries);
        });

        const handleClick = (params: any) => {
            if (params.componentType === 'series' && params.seriesType === 'bar') {
                const fullData = data[params.dataIndex];
                const labelSearch = fullData.labelSearch;

                if (chartQuery.output === 'timeline_by_date') {
                    const defChartQuery = {...finalChartQuery};

                    const eventDatetimeString = `event_datetime:[${labelSearch}]`;
                    let fieldToQueryString;

                    if (chartQuery.fields_to_query) {
                        if (chartQuery.fields_to_query === 'all_fields') {
                            fieldToQueryString = finalChartQuery.query_string;
                        } else {
                            fieldToQueryString = `${chartQuery.fields_to_query}:(${finalChartQuery.query_string})`;
                        }
                    } else {
                        if (fieldToQuery === 'all_fields') {
                            fieldToQueryString = finalChartQuery.query_string;
                        } else {
                            fieldToQueryString = `${fieldToQuery}:(${finalChartQuery.query_string})`;
                        }
                    }

                    defChartQuery.query_string = `${fieldToQueryString} AND ${eventDatetimeString}`;

                    defChartQuery.fields_to_query = ChartFieldToQueryOption.AllFields;
                    const url = `${window.URLS.searchUrl}?${serialize(defChartQuery)}`;

                    window.open(url, '_blank');
                }
            }
        };

        chartInstance.on('click', handleClick);

        return () => {
            if (chartInstance && !chartInstance.isDisposed()) {
                chartInstance.off('click', handleClick);
            }
        };

    }, [data, distinctKeysArray, chartQuery, fieldToQuery, finalChartQuery, comparablePeriod, movingAverage, linearRegression, xAxisName, barOrientation, dataLabelPosition, isHorizontal, isTermsOutput, showTimelineOverlays, chartGrid, showLegend, showToolbox, title, description, datePeriod, legendData, gridBottom, height, width, chartSettingsView, stackedLabelContext, overlayLineLabelConfig, linePointSymbol, categoryAxisLabelConfig, rotateVerticalCategoryLabels, verticalCategoryLabels]);

    return <div ref={chartRef} style={{width: '100%', height: chartHeight}} />;
}

export default BarCharts;